import { AfterViewInit, Component, DestroyRef, inject, Inject, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { FleetService } from '../../../shared/services/fleet.service';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, debounceTime, map, merge, Observable, of, startWith, Subject, tap } from 'rxjs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AutocompleteComponent } from '../../../shared/components/autocomplete/autocomplete.component';
import { TruckSpec } from '../../../shared/models/tire/TruckSpec';
import {MatFormField, MatLabel} from "@angular/material/input";
import {MatSelect} from "@angular/material/select";
import {MatOption} from "@angular/material/core";

@Component({
  selector: 'tkc-add-new-fleet-modal',
  standalone: true,
  templateUrl: './add-new-fleet-modal.component.html',
  styleUrls: ['./add-new-fleet-modal.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatCheckbox,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRow,
    MatRow,
    MatRowDef,
    MatHeaderRowDef,
    MatPaginator,
    MatSort,
    MatSortModule,
    TranslatePipe,
    MatProgressSpinner,
    AutocompleteComponent,
    MatFormField,
    MatSelect,
    MatOption,
    MatLabel
  ]
})
export class AddNewFleetModalComponent implements OnInit, AfterViewInit {
  readonly dialogRef = inject(MatDialogRef<AddNewFleetModalComponent>);
  private FleetService = inject(FleetService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      siteId: number;
      existingTruckSpecIds: number[];
    }
  ) {}

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = ['select', 'seat', 'manufacturer', 'model'];
  dataSource = new MatTableDataSource<TruckSpec>();

  private allTrucks: TruckSpec[] = [];
  private filtersChanged$ = new Subject<void>();

  allSeats: number[] = [];
  allManufacturers: string[] = [];

  selectedSeat?: number;
  selectedManufacturer?: string;

  filteredSeats: number[] = [];
  filteredManufacturers: string[] = [];

  filteredTrucks$!: Observable<TruckSpec[]>;

  filterCtrl = new FormControl<string>('');
  isLoading = false;
  isTableLoading = true;

  isSubmitting = false;

  selection: TruckSpec[] = [];

  pageSize = 10;
  pageSizeOptions = [10, 15, 30];

  ngOnInit() {
    this.isTableLoading = true;
    this.getAllTrucks();
  }

  getAllTrucks() {
    this.FleetService.getAllTruckSpecs()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          const existingIds = this.data.existingTruckSpecIds ?? [];
          const filteredData = data.filter((truck) => !existingIds.includes(truck.truckSpecPk));

          this.allTrucks = filteredData;
          this.dataSource.data = filteredData;

          this.allSeats = Array.from(new Set(filteredData.map((x) => x.seat))).sort((a, b) => a - b);
          this.allManufacturers = Array.from(new Set(filteredData.map((x) => x.manufacturer))).sort();

          this.filteredSeats = [...this.allSeats];
          this.filteredManufacturers = [...this.allManufacturers];

          this.setupAutocomplete();

          this.dataSource.filterPredicate = (data, filter) => {
            const f = JSON.parse(filter || '{}');
            const seatMatch = f.seat === undefined ? true : data.seat === f.seat;
            const manuMatch = f.manufacturer === undefined ? true : data.manufacturer === f.manufacturer;
            const search = f.search === undefined ? undefined : String(f.search).toLowerCase();
            const searchMatch = search ? data.model.toLowerCase().includes(search) : true;
            return seatMatch && manuMatch && searchMatch;
          };
        },
        error: () => {
          this.isTableLoading = false;
          this.cdr.detectChanges();
        },
        complete: () => {
          this.isTableLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private getBaseFilteredTrucks(search?: string): TruckSpec[] {
    return this.allTrucks.filter(
      (t) =>
        (this.selectedSeat === undefined || t.seat === this.selectedSeat) &&
        (this.selectedManufacturer === undefined || t.manufacturer === this.selectedManufacturer) &&
        (!search || t.model.toLowerCase().includes(search))
    );
  }

  private setupAutocomplete() {
    this.filteredTrucks$ = merge(this.filterCtrl.valueChanges, this.filtersChanged$).pipe(
      startWith(null),
      tap(() => {
        this.isLoading = true;
        this.cdr.detectChanges();
      }),
      debounceTime(300),
      map(() => (this.filterCtrl.value ?? '').toLowerCase()),
      map((search) => {
        const results = this.getBaseFilteredTrucks(search);
        this.recomputeAvailableFilters(search);
        this.applySearchFilter(search);
        this.isLoading = false;
        this.cdr.detectChanges();
        return results.slice(0, 10);
      }),
      catchError(() => {
        this.isLoading = false;
        this.cdr.detectChanges();
        return of([]);
      })
    );
  }

  private recomputeAvailableFilters(search?: string) {
    const searchLower = (search ?? '').toLowerCase();

    const baseForSeats = this.allTrucks.filter(
      (truckSpec) =>
        (this.selectedManufacturer === undefined || truckSpec.manufacturer === this.selectedManufacturer) &&
        (!searchLower || truckSpec.model.toLowerCase().includes(searchLower))
    );
    this.filteredSeats = Array.from(new Set(baseForSeats.map((truckSpec) => truckSpec.seat))).sort((a, b) => a - b);

    const baseForManufacturers = this.allTrucks.filter(
      (truckSpec) =>
        (this.selectedSeat === undefined || truckSpec.seat === this.selectedSeat) &&
        (!searchLower || truckSpec.model.toLowerCase().includes(searchLower))
    );
    this.filteredManufacturers = Array.from(new Set(baseForManufacturers.map((truckSpec) => truckSpec.manufacturer))).sort();

    if (this.selectedSeat !== undefined && !this.filteredSeats.includes(this.selectedSeat)) {
      this.selectedSeat = undefined;
    }
    if (this.selectedManufacturer !== undefined && !this.filteredManufacturers.includes(this.selectedManufacturer)) {
      this.selectedManufacturer = undefined;
    }
  }

  protected onElementSelected(truck: TruckSpec) {
    this.filterCtrl.setValue(truck.model, { emitEvent: false });
    this.applySearchFilter(truck.model.toLowerCase());
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  private applySearchFilter(value: string) {
    const filter = {
      seat: this.selectedSeat,
      manufacturer: this.selectedManufacturer,
      search: value || undefined
    };
    this.dataSource.filter = JSON.stringify(filter);
    this.dataSource.paginator?.firstPage();
    this.refreshTable();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.cdr.detectChanges();
  }

  private applyGlobalFilter() {
    const search = (this.filterCtrl.value as string | null)?.toLowerCase() || undefined;
    const filter = {
      seat: this.selectedSeat,
      manufacturer: this.selectedManufacturer,
      search
    };
    this.dataSource.filter = JSON.stringify(filter);
    this.dataSource.paginator?.firstPage();
    this.refreshTable();
  }

  applySeatFilter() {
    this.filtersChanged$.next();
    this.applyGlobalFilter();
  }

  applyManufacturerFilter() {
    this.filtersChanged$.next();
    this.applyGlobalFilter();
  }

  clearSeat() {
    this.selectedSeat = undefined;
    this.filtersChanged$.next();
    this.applyGlobalFilter();
  }

  clearManufacturer() {
    this.selectedManufacturer = undefined;
    this.filtersChanged$.next();
    this.applyGlobalFilter();
  }

  toggleSelection(row: TruckSpec) {
    const index = this.selection.indexOf(row);
    index >= 0 ? this.selection.splice(index, 1) : this.selection.push(row);
  }

  private refreshTable() {
    this.dataSource.filter = this.dataSource.filter;
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  addFleet() {
    if (this.isSubmitting || !this.data?.siteId || this.selection.length === 0) {
      return;
    }
    this.isSubmitting = true;
    this.dialogRef.close({ selection: this.selection });
  }

  closeModal() {
    this.dialogRef.close();
  }
}
